/** Mutations for a per-TO preflight item (status / step / preview / run / execute /
 *  complete / configure), shared by the Preflight tab and the system-wide Items drawer.
 *
 *  All calls hit /tour_operators/{topId}/preflight_items/… so the caller passes the
 *  item's tenant id as `topId`. `extraInvalidate` lets a caller refresh its own list
 *  query (e.g. the cross-tenant Items grid) on top of the per-TO caches. */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { fieldErrorsOf, items, type PreflightActionRun, type PreflightStatus } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';

export interface PreflightItemActions {
  /** Any mutation in flight, for hosts that show one item. */
  pending: boolean;
  /** …and which item it is. Saving one row's fields must not grey out every
   *  other row's Save button, which a single shared flag did. */
  isBusy: (id: number) => boolean;
  previewRun: PreflightActionRun | null;
  clearPreview: () => void;
  invalidate: () => void;
  onStatusChange: (id: number, status: PreflightStatus) => void;
  onStepChange: (id: number, step: number, status?: PreflightStatus) => void;
  onPreview: (id: number) => void;
  /** POST run/ — for items whose `run_action` is 'run'. */
  onRun: (id: number) => void;
  /** POST execute/ — for items whose `run_action` is 'execute'. Same reporting
   *  as `onRun`; a different endpoint because an ops click is not the
   *  kill-switch-gated unattended sweep. */
  onExecute: (id: number) => void;
  onComplete: (id: number) => void;
  /** Resolves with the configure run; REJECTS with the API error, so the field
   *  editor can anchor a per-key `{errors: {…}}` 400 to the field that caused it. */
  onConfigure: (id: number, values: Record<string, unknown>) => Promise<PreflightActionRun>;
  /** The last configure run this page recorded for the item, until dismissed. Its
   *  `summary` is the save's verdict (a key that would not verify, a key another tenant
   *  holds) — more than a snackbar's few seconds can carry. Held here, not in the panel,
   *  so it survives the panel moving (the Parameters step re-sorts an item that stops
   *  being ready into "Required to run"). */
  lastConfigureRun: (id: number) => PreflightActionRun | null;
  dismissConfigureRun: (id: number) => void;
}

/** `mutateAsync` rejects; every mutation already reports through `onError`, so the
 *  fire-and-forget callers swallow the rejection rather than leaving it unhandled.
 *  `onConfigure` is the exception — the field editor wants it. */
const noop = () => undefined;

export function usePreflightItemActions(topId: number, extraInvalidate?: () => void): PreflightItemActions {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const [previewRun, setPreviewRun] = useState<PreflightActionRun | null>(null);
  // The item a mutation is currently in flight for. One shared `pending` flag
  // disabled every item on the page while any one of them saved.
  const [busyId, setBusyId] = useState<number | null>(null);
  const [configureRuns, setConfigureRuns] = useState<ReadonlyMap<number, PreflightActionRun>>(new Map());
  const rememberConfigureRun = (id: number, run: PreflightActionRun | null) =>
    setConfigureRuns((cur) => {
      const next = new Map(cur);
      if (run) next.set(id, run);
      else next.delete(id);
      return next;
    });
  const track = <T,>(id: number, p: Promise<T>): Promise<T> => {
    setBusyId(id);
    return p.finally(() => setBusyId((cur) => (cur === id ? null : cur)));
  };

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['preflight', 'items', topId] });
    void qc.invalidateQueries({ queryKey: ['preflight', 'item-runs', topId] });
    extraInvalidate?.();
  };
  const onError = (e: unknown) =>
    snackbar.show(e instanceof ConsoleApiError ? e.message : 'Request failed.', 'error');

  const patchMutation = useMutation({
    mutationFn: ({ id, status, step }: { id: number; status?: PreflightStatus; step?: number }) =>
      items.patch(topId, id, { status, step }),
    onSuccess: () => {
      snackbar.show('Preflight item updated.', 'success');
      invalidate();
    },
    onError,
  });

  const previewMutation = useMutation({
    mutationFn: (id: number) => items.preview(topId, id),
    onSuccess: (run) => {
      setPreviewRun(run);
      invalidate();
    },
    onError,
  });

  const runMutation = useMutation({
    mutationFn: (id: number) => items.run(topId, id),
    onSuccess: (resp) => {
      snackbar.show(
        resp.enqueued
          ? 'Action enqueued — it will run in the background.'
          : `Action run: ${resp.run?.summary ?? 'done.'}`,
        resp.run?.status === 'error' ? 'error' : 'success',
      );
      invalidate();
    },
    onError,
  });

  const executeMutation = useMutation({
    mutationFn: (id: number) => items.execute(topId, id),
    onSuccess: (resp) => {
      snackbar.show(
        resp.enqueued
          ? 'Action enqueued — it will run in the background.'
          : `Action run: ${resp.run?.summary ?? 'done.'}`,
        resp.run?.status === 'error' ? 'error' : 'success',
      );
      invalidate();
    },
    onError,
  });

  // `complete/` answers 200 with the recorded run even when the action REFUSED
  // (on_manual_complete returned failed()), so the run's own status — not the
  // HTTP status — decides what the operator is told.
  const completeMutation = useMutation({
    mutationFn: (id: number) => items.complete(topId, id),
    onSuccess: (run) => {
      const refused = run.status === 'error';
      snackbar.show(
        refused ? run.error_message || 'The action refused to complete.' : 'Item marked complete.',
        refused ? 'error' : 'success',
      );
      invalidate();
    },
    onError,
  });

  const configureMutation = useMutation({
    mutationFn: ({ id, values }: { id: number; values: Record<string, unknown> }) =>
      items.configure(topId, id, values),
    onSuccess: (run, { id }) => {
      snackbar.show(
        run.status === 'error' ? run.error_message || 'Update failed.' : 'Values saved.',
        run.status === 'error' ? 'error' : 'success',
      );
      rememberConfigureRun(id, run);
      invalidate();
    },
    // A per-field 400 is rendered on the fields themselves; a snackbar saying the
    // same thing a second time, with no anchor, is noise.
    onError: (e) => {
      if (!fieldErrorsOf(e)) onError(e);
    },
  });

  return {
    pending: busyId !== null,
    isBusy: (id) => busyId === id,
    previewRun,
    clearPreview: () => setPreviewRun(null),
    invalidate,
    onStatusChange: (id, status) => void track(id, patchMutation.mutateAsync({ id, status })).catch(noop),
    onStepChange: (id, step, status) =>
      void track(id, patchMutation.mutateAsync({ id, step, status })).catch(noop),
    onPreview: (id) => void track(id, previewMutation.mutateAsync(id)).catch(noop),
    onRun: (id) => void track(id, runMutation.mutateAsync(id)).catch(noop),
    onExecute: (id) => void track(id, executeMutation.mutateAsync(id)).catch(noop),
    onComplete: (id) => void track(id, completeMutation.mutateAsync(id)).catch(noop),
    onConfigure: (id, values) => track(id, configureMutation.mutateAsync({ id, values })),
    lastConfigureRun: (id) => configureRuns.get(id) ?? null,
    dismissConfigureRun: (id) => rememberConfigureRun(id, null),
  };
}
